import { MessageSquareQuote } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CtaBanner } from "@/components/CtaBanner";
import { ReviewCard } from "@/components/ReviewCard";
import { ReviewForm } from "@/components/ReviewForm";
import { Stars } from "@/components/Stars";
import { useReviews } from "@/hooks/useReviews";

export default function ReviewsPage() {
  const { items, total, averageRating, loading } = useReviews(100);

  return (
    <>
      <Seo
        title="Client Reviews | Tedmo Services"
        description="Read what clients say about working with Tedmo Services on application development, cloud infrastructure, server deployment, and ongoing support — or leave your own review."
        path="/reviews"
      />

      <section className="py-20 border-b border-line" data-testid="reviews-hero">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10 items-end">
          <div className="md:col-span-2">
            <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
              Client Reviews
            </span>
            <h1
              data-testid="reviews-page-title"
              className="font-poster text-4xl sm:text-5xl font-extrabold text-steel mt-2 max-w-3xl leading-tight"
            >
              What clients say about working with us
            </h1>
            <p className="text-lg text-ink mt-6 max-w-2xl leading-relaxed">
              Real feedback from the businesses we build, deploy, and support
              software for. Every review is submitted by a client and published
              after our team verifies it.
            </p>
          </div>
          <div
            data-testid="reviews-summary"
            className="rounded-2xl border border-line bg-white p-6 shadow-sm"
          >
            {total > 0 ? (
              <>
                <p className="font-poster text-5xl font-extrabold text-steel leading-none">
                  {averageRating.toFixed(1)}
                </p>
                <Stars rating={Math.round(averageRating)} className="mt-3" />
                <p className="mt-2 font-mono-sub text-xs text-mute">
                  Average from {total} published review{total === 1 ? "" : "s"}
                </p>
              </>
            ) : (
              <>
                <MessageSquareQuote className="h-8 w-8 text-royal" aria-hidden="true" />
                <p className="mt-3 font-poster text-lg font-bold text-steel">No published reviews yet</p>
                <p className="mt-1 text-sm text-ink leading-relaxed">
                  We only show verified client feedback — worked with us? Be the first to share.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 max-w-6xl mx-auto px-6" data-testid="reviews-list">
        {loading ? (
          <p className="font-mono-sub text-xs text-mute" data-testid="reviews-loading">Loading reviews…</p>
        ) : items.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((review, i) => (
              <Reveal key={review.id} delay={(i % 3) * 60}>
                <ReviewCard review={review} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div
            data-testid="reviews-empty-state"
            className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-12 text-center"
          >
            <p className="font-poster text-xl font-bold text-steel">Your review could be the first</p>
            <p className="mt-2 text-sm text-ink max-w-md mx-auto leading-relaxed">
              We don't invent testimonials. Reviews appear here only after a real client submits one and we approve it.
            </p>
          </div>
        )}
      </section>

      <section id="write-review" className="py-20 bg-white border-y border-line" data-testid="review-form-section">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal className="mb-10 text-center">
            <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
              Worked with us?
            </span>
            <h2 className="font-poster text-3xl font-bold text-steel mt-2">Leave a review</h2>
            <p className="text-sm text-ink mt-3 max-w-xl mx-auto leading-relaxed">
              Tell other businesses what it was like to work with Tedmo Services.
              Your email stays private and is only used to confirm you are a client.
            </p>
          </Reveal>
          <Reveal>
            <ReviewForm />
          </Reveal>
        </div>
      </section>

      <CtaBanner title="Want to be our next success story?" />
    </>
  );
}
